/**
 * 应用模块管理系统
 * 统一管理功能模块的生命周期,避免在 JSX 中混入非显示逻辑
 */

export interface AppModule {
  /** 模块名称 */
  name: string;

  /** 加载优先级 (数字越小优先级越高) */
  priority: number;

  /** 初始化模块 */
  init(): Promise<void> | void;

  /** 清理模块资源 (可选) */
  destroy?(): void;
}

export class ModuleManager {
  private static instance: ModuleManager | null = null;
  private modules: Map<string, AppModule> = new Map();
  private initialized = false;

  private constructor() {}

  /** 获取单例实例 */
  static getInstance(): ModuleManager {
    if (!ModuleManager.instance) {
      ModuleManager.instance = new ModuleManager();
    }
    return ModuleManager.instance;
  }

  /**
   * 注册模块
   * @param module 要注册的模块
   */
  register(appModule: AppModule): void {
    if (this.modules.has(appModule.name)) {
      return;
    }

    this.modules.set(appModule.name, appModule);
  }

  /**
   * 批量注册模块
   * @param modules 要注册的模块数组
   */
  registerMultiple(modules: AppModule[]): void {
    modules.forEach(appModule => this.register(appModule));
  }

  /**
   * 初始化所有已注册的模块
   * 按优先级排序后依次初始化
   */
  async initializeAll(): Promise<void> {
    if (this.initialized) {
      return;
    }

    // 按优先级排序
    const sortedModules = Array.from(this.modules.values()).sort(
      (a, b) => a.priority - b.priority
    );

    // 依次初始化
    for (const appModule of sortedModules) {
      try {
        await appModule.init();
      } catch (error) {
        // 继续初始化其他模块,不中断流程
      }
    }

    this.initialized = true;
  }

  /**
   * 清理所有模块
   */
  destroyAll(): void {
    // 按优先级逆序清理
    const sortedModules = Array.from(this.modules.values()).sort(
      (a, b) => b.priority - a.priority
    );

    for (const appModule of sortedModules) {
      try {
        if (appModule.destroy) {
          appModule.destroy();
        }
      } catch (error) {
        // Silently handle errors during cleanup
      }
    }

    this.modules.clear();
    this.initialized = false;
  }

  /**
   * 获取指定模块
   * @param name 模块名称
   */
  getModule(name: string): AppModule | undefined {
    return this.modules.get(name);
  }

  /**
   * 检查模块是否已注册
   * @param name 模块名称
   */
  hasModule(name: string): boolean {
    return this.modules.has(name);
  }

  /**
   * 获取所有已注册的模块名称
   */
  getModuleNames(): string[] {
    return Array.from(this.modules.keys());
  }
}

// 导出单例实例
export const moduleManager = ModuleManager.getInstance();
